import React from 'react';
import {
    alpha,
    Box,
    Paper,
    Skeleton,
    SxProps,
    Theme,
    Typography,
} from '@mui/material';
import { formatFloat } from '../../../../helpers';

export interface IOwnAccountBalance {
    account_id?: number | null;
    current_amount?: number | null;
    account?: {
        name?: string | null;
    } | null;
}

interface ICOwnAccountsBalancesProps {
    accounts: IOwnAccountBalance[];
    title?: string;
    showTotal?: boolean;
    // While true the widget shows placeholder tiles instead of the balances.
    // Lets every consumer keep the layout (and reserve the space) during its
    // own data load without re-implementing the skeleton.
    loading?: boolean;
    sx?: SxProps<Theme>;
    titleSx?: SxProps<Theme>;
}

// Shared "cuentas propias" balance tiles — used on the Transferencias and
// Saldos y pagos pages. Accounts with a zero balance (rounded to cents, so
// float noise near zero is treated as zero) are hidden.
export function COwnAccountsBalances({
    accounts,
    title = 'Cuentas propias',
    showTotal = true,
    loading = false,
    sx,
    titleSx,
}: ICOwnAccountsBalancesProps) {
    const nonZeroAccounts = accounts.filter(
        (ats) => Math.round((ats.current_amount ?? 0) * 100) !== 0,
    );

    // Loading wins over the empty-state null so the widget keeps its footprint
    // (and any flex sizing from `sx`) while the consumer's query is in flight.
    if (loading) {
        return (
            <Box sx={sx}>
                <Skeleton
                    variant={'text'}
                    width={120}
                    sx={{ mb: 0.5, ...titleSx }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                    {[0, 1, 2].map((i) => (
                        <Skeleton
                            key={i}
                            variant={'rectangular'}
                            width={140}
                            height={32}
                            sx={{ borderRadius: 4 }}
                        />
                    ))}
                </Box>
            </Box>
        );
    }

    if (nonZeroAccounts.length === 0) {
        return null;
    }

    return (
        <Box sx={sx}>
            <Typography
                variant="overline"
                sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1, ...titleSx }}
            >
                {title}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                {nonZeroAccounts.map((ats, index) => {
                    const amount = ats.current_amount ?? 0;
                    const positive = amount >= 0;
                    const accent = positive ? 'success.main' : 'error.main';
                    return (
                        <Paper
                            key={ats.account_id ?? index}
                            elevation={0}
                            sx={{
                                position: 'relative',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                pl: 1.25,
                                pr: 1,
                                py: 0.5,
                                borderRadius: 4,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: (theme: Theme) =>
                                    alpha(
                                        positive
                                            ? theme.palette.success.main
                                            : theme.palette.error.main,
                                        0.07,
                                    ),
                            }}
                        >
                            <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                    color: 'text.secondary',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                    fontWeight: 600,
                                }}
                            >
                                {ats.account?.name}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: accent, lineHeight: 1.2 }}
                            >
                                {`$${formatFloat(amount)}`}
                            </Typography>
                        </Paper>
                    );
                })}
                {showTotal && nonZeroAccounts.length > 1 && (
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            pl: 1.25,
                            pr: 1,
                            py: 0.5,
                            borderRadius: 4,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                                fontWeight: 600,
                            }}
                        >
                            Total
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}
                        >
                            {`$${formatFloat(
                                nonZeroAccounts.reduce((acc, ats) => acc + (ats.current_amount ?? 0), 0),
                            )}`}
                        </Typography>
                    </Paper>
                )}
            </Box>
        </Box>
    );
}
